import _isNaN from 'lodash/isNaN';
import _isNil from 'lodash/isNil';

export const isNumberOrStringifiedNumber = (value: number | string | null | undefined): boolean =>
  !_isNil(value) && !_isNaN(Number(value));

/*
 * WARNING: This function is exposed in the no-code platform utilities.
 * Any API changes must maintain backward compatibility.
 * Documentation updates must be made in packages/carbon/docs/website/utilities.md
 * before deploying breaking changes.
 */
export const formatNumber = ({
  value,
  locale,
  maximumFractionDigits,
  maximumSignificantDigits,
  notation,
  compactDisplay,
  currencyDisplay,
  unit,
  padDecimalPlaces,
  unitDisplay,
}: {
  value: number;
  locale?: string;
  maximumFractionDigits?: number;
  maximumSignificantDigits?: number;
  notation?: 'standard' | 'scientific' | 'engineering' | 'compact' | 'text';
  compactDisplay?: 'short' | 'long';
  currencyDisplay?: 'code' | 'symbol' | 'narrowSymbol' | 'name';
  unit?: string;
  padDecimalPlaces?: boolean;
  unitDisplay?: 'short' | 'narrow' | 'long';
}) => {
  try {
    if (notation === 'text') {
      return new Intl.NumberFormat(locale, {
        useGrouping: false,
      }).format(value);
    }

    return new Intl.NumberFormat(locale, {
      maximumFractionDigits,
      maximumSignificantDigits,
      minimumFractionDigits: padDecimalPlaces ? maximumFractionDigits : undefined,
      notation,
      compactDisplay,
      style: unit ? 'unit' : 'decimal',
      currencyDisplay,
      unit,
      unitDisplay,
    }).format(value);
  } catch (e) {
    return String(value);
  }
};

/**
 * Formats a token count value with compact notation for large numbers.
 * Returns the formatted string and whether a tooltip should be shown.
 * @param value - The token count value to format
 * @returns Object with formatted string and needsTooltip flag
 */
export const getFormattedTokenCount = (
  value: number,
): { formatted: string; needsTooltip: boolean } => {
  if (value < 1000) {
    return { formatted: String(value), needsTooltip: false };
  }
  const formatted = formatNumber({
    value,
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  });
  return { formatted, needsTooltip: true };
};
